import { AlertTriangle, FolderOpen, ShoppingCart } from 'lucide-react';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import type { DashboardAlert } from '../../types/dashboard';

type DashboardAlertsPanelProps = {
  error: string | null;
  loading: boolean;
  onOpen: (alert: DashboardAlert) => void;
  alerts: DashboardAlert[];
};

const ALERT_VARIANTS: Record<DashboardAlert['type'], 'secondary' | 'quoted' | 'inProgress' | 'destructive'> = {
  order_email_overdue: 'quoted',
  order_call_overdue: 'inProgress',
  order_confirm_overdue: 'destructive',
  subproject_pending_items_overdue: 'secondary',
};

const ALERT_LABELS: Record<DashboardAlert['type'], string> = {
  order_email_overdue: 'Email pendiente',
  order_call_overdue: 'Llamada pendiente',
  order_confirm_overdue: 'Confirmación pendiente',
  subproject_pending_items_overdue: 'Objetos pendientes',
};

const formatElapsed = (elapsedHours: number) => {
  if (elapsedHours < 24) return `${elapsedHours}h`;
  const days = Math.floor(elapsedHours / 24);
  const hours = elapsedHours % 24;
  return `${days}d ${hours}h`;
};

function AlertsSummary({ alerts }: { alerts: DashboardAlert[] }) {
  const alertCounts = alerts.reduce<Record<DashboardAlert['type'], number>>(
    (counts, alert) => ({
      ...counts,
      [alert.type]: (counts[alert.type] ?? 0) + 1,
    }),
    {
      order_email_overdue: 0,
      order_call_overdue: 0,
      order_confirm_overdue: 0,
      subproject_pending_items_overdue: 0,
    },
  );

  return (
    <div className="flex flex-wrap gap-2">
      {Object.entries(alertCounts)
        .filter(([, count]) => count > 0)
        .map(([type, count]) => (
          <Badge key={type} variant={ALERT_VARIANTS[type as DashboardAlert['type']]} className="gap-2 px-2.5 py-1">
            <span>{ALERT_LABELS[type as DashboardAlert['type']]}</span>
            <span>{count}</span>
          </Badge>
        ))}
    </div>
  );
}

function AlertsSection({
  alerts,
  icon: Icon,
  onOpen,
  title,
}: {
  alerts: DashboardAlert[];
  icon: typeof ShoppingCart;
  onOpen: (alert: DashboardAlert) => void;
  title: string;
}) {
  const visibleAlerts = alerts.slice(0, 5);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-gray-600" />
          <h3 className="text-sm font-medium text-gray-900">{title}</h3>
        </div>
        <Badge variant="secondary">{alerts.length}</Badge>
      </div>

      {alerts.length === 0 ? (
        <p className="text-sm text-gray-500">No hay alertas activas.</p>
      ) : (
        <div className="space-y-3">
          {visibleAlerts.map((alert) => (
            <div key={alert.id} className="rounded-lg border border-gray-200 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={ALERT_VARIANTS[alert.type]}>{ALERT_LABELS[alert.type]}</Badge>
                <span className="text-xs text-gray-500">Vencida hace {formatElapsed(alert.elapsed_hours)}</span>
              </div>
              <div className="mt-3 space-y-1">
                <p className="text-sm font-medium text-gray-900">{alert.title_es}</p>
                <p className="text-sm text-gray-600">{alert.description_es}</p>
                <p className="text-xs text-gray-500">
                  {alert.project_name}
                  {alert.subproject_name ? ` · ${alert.subproject_name}` : ''}
                </p>
              </div>
              <div className="mt-4 flex justify-end">
                <Button size="sm" variant="outline" onClick={() => onOpen(alert)}>
                  Abrir
                </Button>
              </div>
            </div>
          ))}
          {alerts.length > visibleAlerts.length ? (
            <p className="text-xs text-gray-500">
              Mostrando {visibleAlerts.length} de {alerts.length} alertas.
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

export function DashboardAlertsPanel({
  alerts,
  error,
  loading,
  onOpen,
}: DashboardAlertsPanelProps) {
  const orderAlerts = alerts.filter((alert) => alert.entity_type === 'order');
  const subprojectAlerts = alerts.filter((alert) => alert.entity_type === 'subproject');

  return (
    <Card className="shadow-sm xl:sticky xl:top-6">
      <CardHeader>
        <CardTitle>Centro de alertas</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {error ? (
          <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>{error}</p>
          </div>
        ) : loading ? (
          <div className="space-y-3">
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : alerts.length === 0 ? (
          <p className="text-sm text-gray-500">No hay alertas activas en este momento.</p>
        ) : (
          <>
            <AlertsSummary alerts={alerts} />
            <AlertsSection title="Pedidos" icon={ShoppingCart} alerts={orderAlerts} onOpen={onOpen} />
            <div className="border-t border-gray-100 pt-6">
              <AlertsSection title="Subproyectos" icon={FolderOpen} alerts={subprojectAlerts} onOpen={onOpen} />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
